import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

// Labeled grouping inside a SettingsSection.  Title is a true
// heading (sentence-case, body-weight, primary color) so it reads
// as a section header rather than just another row label; the
// rows themselves are wrapped in a bordered sub-surface so the
// parent-child relationship is unambiguous.
//
// `chip` renders an MUI Chip next to the title — same pattern as
// SettingsSection.  Use it to flag a subset of fields gated on a
// specific role (e.g. "Superuser Only" on the is_superuser toggle
// row).
//
// `bordered` opts in to a row-container surface — pair with
// SettingsFieldRow children for the Stripe/Linear-style nested
// settings idiom.  Off by default so subsections rendering their
// own custom layout (alarm grid, list fields, etc.) aren't
// double-wrapped.
//
// `flush` removes the bordered container's internal padding so
// children can run edge-to-edge against the border (useful for
// list/grid layouts where you want the dividers to span the full
// width of the container).  No effect when `bordered` is false.
export default function SettingsSubsection({
  title,
  description,
  chip,
  bordered = false,
  flush = false,
  children,
  sx,
}) {
  return (
    <Box sx={{ pt: 2, ...sx }}>
      <Stack spacing={0.25} sx={{ pb: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          {chip}
        </Stack>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
      </Stack>
      {bordered ? (
        <Box
          sx={{
            borderRadius: 'var(--card-radius)',
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            ...(flush ? {} : { px: { xs: 1.5, sm: 2 }, py: 0.5 }),
          }}
        >
          {children}
        </Box>
      ) : (
        <Box>{children}</Box>
      )}
    </Box>
  );
}
